import React from 'react'
import { WorkingStatus } from '../StepContent/WorkingStatus'
import { ExperienceYears } from '../StepContent/ExperienceYears'
import { LoginInformation } from '../StepContent/LoginInformation'
import { EmailOnly } from '../StepContent/EmailOnly'
import { PasswordOnly } from '../StepContent/PasswordOnly'
import { Birthday } from '../StepContent/Birthday'
import { CompanyName } from '../StepContent/CompanyName'
import { EmploymentStatus } from '../StepContent/EmploymentStatus'
import { Sex } from '../StepContent/Sex'
import { Prefecture } from '../StepContent/Prefecture'
import { FullName } from '../StepContent/FullName'
import { Tel } from '../StepContent/Tel'
import { EnrollmentPeriod } from '../StepContent/EnrollmentPeriod'
import { DetailedWorkDetails } from '../StepContent/DetailedWorkDetails'
import { ContentConfirmation } from '../StepContent/ContentConfirmation'
import { CuisineGenre } from '../StepContent/CuisineGenre/CuisineGenre'
import { JobContent } from '../StepContent/JobContent/JobContent'

export type SignupStep = {
    Component: React.ComponentType
    // 3分割ステップバー（B/Cのみ使用）: 0=基本情報 1=経歴 3=全セクション完了（確認画面）。
    // 2（3つ目のドットが現在地）は、確認画面の手前に別セクションを置く構成になったときだけ使う
    sectionDot: number
}

// DEV-3482: B/Cはメール・パスワードを独立ステップに分けるため16ステップになる。
// 画面の見た目はAと同じ部品で、違いはこの並び順と番号付きステッパー（SignupNumberedStepper）だけ

// 確認画面に与える sectionDot。SignupNumberedStepper の TOTAL_DOTS と同値にすると、
// getDotStatus が追加の特殊分岐なしに全ドット完了扱いで描画する
// （確認画面＝全セクション完了の表現。DEV-3012と同じ方式）
const SECTION_DOT_COMPLETED = 3

// Aパターン: 現行の並び順（15ステップ）。sectionDotはAでは参照されない（未使用のため全て0で埋める）
const SIGNUP_STEPS_A: SignupStep[] = [
    { Component: WorkingStatus, sectionDot: 0 },
    { Component: ExperienceYears, sectionDot: 0 },
    { Component: Sex, sectionDot: 0 },
    { Component: Birthday, sectionDot: 0 },
    { Component: Prefecture, sectionDot: 0 },
    { Component: FullName, sectionDot: 0 },
    { Component: Tel, sectionDot: 0 },
    { Component: LoginInformation, sectionDot: 0 },
    { Component: EmploymentStatus, sectionDot: 0 },
    { Component: CompanyName, sectionDot: 0 },
    { Component: EnrollmentPeriod, sectionDot: 0 },
    { Component: CuisineGenre, sectionDot: 0 },
    { Component: JobContent, sectionDot: 0 },
    { Component: DetailedWorkDetails, sectionDot: 0 },
    { Component: ContentConfirmation, sectionDot: 0 },
]

// Bパターン: メール/パスワードを先頭に（AG会員転記のためメールを1問目に置く）。16ステップ
const SIGNUP_STEPS_B: SignupStep[] = [
    { Component: EmailOnly, sectionDot: 0 },
    { Component: PasswordOnly, sectionDot: 0 },
    { Component: FullName, sectionDot: 0 },
    { Component: Sex, sectionDot: 0 },
    { Component: Birthday, sectionDot: 0 },
    { Component: Tel, sectionDot: 0 },
    { Component: Prefecture, sectionDot: 0 },
    { Component: WorkingStatus, sectionDot: 1 },
    { Component: ExperienceYears, sectionDot: 1 },
    { Component: EmploymentStatus, sectionDot: 1 },
    { Component: CompanyName, sectionDot: 1 },
    { Component: EnrollmentPeriod, sectionDot: 1 },
    { Component: CuisineGenre, sectionDot: 1 },
    { Component: JobContent, sectionDot: 1 },
    { Component: DetailedWorkDetails, sectionDot: 1 },
    { Component: ContentConfirmation, sectionDot: SECTION_DOT_COMPLETED },
]

// Cパターン: Aの並びのまま、メール+パスワードの1画面（LoginInformation）だけを2画面に分ける。
// 転記は行わないため、メールの位置はAと同じ（氏名・電話の後ろ）
const SIGNUP_STEPS_C: SignupStep[] = [
    { Component: WorkingStatus, sectionDot: 0 },
    { Component: ExperienceYears, sectionDot: 0 },
    { Component: Sex, sectionDot: 0 },
    { Component: Birthday, sectionDot: 0 },
    { Component: Prefecture, sectionDot: 0 },
    { Component: FullName, sectionDot: 0 },
    { Component: Tel, sectionDot: 0 },
    { Component: EmailOnly, sectionDot: 0 },
    { Component: PasswordOnly, sectionDot: 0 },
    { Component: EmploymentStatus, sectionDot: 1 },
    { Component: CompanyName, sectionDot: 1 },
    { Component: EnrollmentPeriod, sectionDot: 1 },
    { Component: CuisineGenre, sectionDot: 1 },
    { Component: JobContent, sectionDot: 1 },
    { Component: DetailedWorkDetails, sectionDot: 1 },
    { Component: ContentConfirmation, sectionDot: SECTION_DOT_COMPLETED },
]

/**
 * @param pattern ABテストのパターン。B/C以外（未配信・Dパターン）はAの並びを返す
 * @returns 表示順に並んだステップ。index がそのまま stepReducer の currentStep になる
 */
export const getSteps = (pattern: 'A' | 'B' | 'C' | 'D' = 'A'): SignupStep[] =>
    pattern === 'B'
        ? SIGNUP_STEPS_B
        : pattern === 'C'
          ? SIGNUP_STEPS_C
          : SIGNUP_STEPS_A

// DEV-3482: AG会員転記が値を入れるステップのindexは
// StepContent/EmailOnly/agPrefillPlan.ts の B_PREFILL_STEP_INDICES にある。
// SIGNUP_STEPS_B を並び替えたら、あちらも合わせて直すこと（Jestで対応を固定している）
